/**
 * Maneuvers Data Service
 * 
 * Unified data fetching service for maneuvers components that abstracts
 * the differences between dataset, fleet, and historical data sources.
 */

import { getData } from '../utils/global';
import { getManeuversConfig, ManeuversContext } from '../utils/maneuversConfig';
import { persistantStore } from '../store/persistantStore';
import { sourcesStore } from '../store/sourcesStore';
import { debug, error as logError, warn as logWarning, log } from '../utils/console';
import { apiEndpoints } from '@config/env';

const { selectedClassName, selectedProjectId } = persistantStore;

/** getData mixes `message` (handleError) and `error` (e.g. abort / some bodies); avoid useless "Unknown error" in logs. */
function getDataFailureReason(result: { message?: string; error?: string; status?: number; type?: string }): string {
  const fromMessage = typeof result.message === 'string' ? result.message.trim() : '';
  if (fromMessage) return fromMessage;
  const fromError = typeof result.error === 'string' ? result.error.trim() : '';
  if (fromError) return fromError;
  if (result.status != null && Number.isFinite(result.status)) {
    return `Request failed (status ${result.status})`;
  }
  const t = typeof result.type === 'string' ? result.type.trim() : '';
  if (t) return t;
  return 'Unknown error';
}

/** In-flight GET aborted (navigation, filter churn, new request superseding old). Not a product failure. */
function isGetDataCancelledResult(result: { type?: string; error?: string }): boolean {
  return result.type === 'AbortError' || result.error === 'Request cancelled';
}

export interface ManeuversDataParams {
  eventType: string;
  /**
   * Description/phase identifier (e.g., '0_normalized', '1_standard', '2_normalized')
   * Required for mapdata and timeseries endpoints
   */
  description?: string;
  /**
   * Explicit list of event_ids to fetch data for.
   * Required for map and time series endpoints when eventList.length <= 100.
   * When eventList.length > 100, timeRange and filters are used instead.
   */
  eventList?: number[];
  /**
   * Time range parameters (used when eventList.length > 100)
   */
  timeRange?: {
    startDate: string;
    endDate: string;
  };
  /**
   * Filter parameters (used when eventList.length > 100)
   */
  filters?: {
    GRADE?: number[];
    YEAR?: number[];
    EVENT?: string[];
    CONFIG?: string[];
    STATE?: string[];
    SOURCE_NAME?: string[];
    /** TRAINING = only training (race_number -1); RACING = exclude training */
    TRAINING_RACING?: string[];
  };
  /**
   * Source names array (required, for fleet/multi-source queries)
   */
  sourceNames?: string[];
  /**
   * Count for history queries (default 5)
   */
  count?: number;
  /**
   * Date for single date queries
   */
  date?: string;
  /**
   * Channels array for table data queries
   */
  channels?: string[];
  signal?: AbortSignal;
}

/**
 * Fetch table data for maneuvers
 */
export async function fetchTableData(
  context: ManeuversContext,
  params: ManeuversDataParams
): Promise<any[]> {
  try {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    
    if (!className || !projectId) {
      logWarning('fetchTableData: className and projectId are required');
      return [];
    }

    // Check if this is a history query (has timeRange) or single date query (has date)
    if (params.timeRange && params.timeRange.startDate && params.timeRange.endDate) {
      // History query: use new /maneuvers-history endpoint
      const sourceNames = params.sourceNames;
      
      if (!sourceNames || sourceNames.length === 0) {
        logWarning('fetchTableData: sourceNames is required for history queries');
        return [];
      }

      const endpoint = `${apiEndpoints.app.data}/maneuvers-history`;
      let url = new URL(endpoint, window.location.origin);
      
      url.searchParams.append('class_name', className);
      url.searchParams.append('project_id', String(projectId));
      url.searchParams.append('source_names', JSON.stringify(sourceNames));
      
      url.searchParams.append('start_date', params.timeRange.startDate);
      url.searchParams.append('end_date', params.timeRange.endDate);
      url.searchParams.append('event_type', params.eventType.toLowerCase());
      
      // Channels are required for table data
      if (params.channels && params.channels.length > 0) {
        url.searchParams.append('channels', JSON.stringify(params.channels));
      } else {
        logWarning('fetchTableData: channels are required for history queries');
        return [];
      }
      
      if (params.count) {
        url.searchParams.append('count', String(params.count));
      }
      
      if (params.filters && Object.keys(params.filters).length > 0) {
        url.searchParams.append('filters', JSON.stringify(params.filters));
      }

      const result = await getData(url.pathname + url.search, params.signal);
      if (result.success && result.data && Array.isArray(result.data)) {
        return result.data;
      } else {
        if (!isGetDataCancelledResult(result)) {
          logWarning('Failed to fetch history table data:', getDataFailureReason(result));
        }
        return [];
      }
    } else if (params.date) {
      // Single date query: use new /maneuvers endpoint
      const sourceNames = params.sourceNames;
      
      if (!sourceNames || sourceNames.length === 0) {
        logWarning('fetchTableData: sourceNames is required for single date queries');
        return [];
      }

      const endpoint = `${apiEndpoints.app.data}/maneuvers`;
      let url = new URL(endpoint, window.location.origin);
      
      url.searchParams.append('class_name', className);
      url.searchParams.append('project_id', String(projectId));
      url.searchParams.append('source_names', JSON.stringify(sourceNames));
      
      url.searchParams.append('date', params.date);
      url.searchParams.append('event_type', params.eventType.toLowerCase());
      
      // Channels are required for table data
      if (params.channels && params.channels.length > 0) {
        url.searchParams.append('channels', JSON.stringify(params.channels));
      } else {
        logWarning('fetchTableData: channels are required for single date queries');
        return [];
      }
      
      // For single date, only allow GRADE, STATE, SOURCE_NAME filters
      if (params.filters) {
        const simpleFilters: any = {};
        if (params.filters.GRADE) simpleFilters.GRADE = params.filters.GRADE;
        if (params.filters.STATE) simpleFilters.STATE = params.filters.STATE;
        if (params.filters.SOURCE_NAME) simpleFilters.SOURCE_NAME = params.filters.SOURCE_NAME;
        
        if (Object.keys(simpleFilters).length > 0) {
          url.searchParams.append('filters', JSON.stringify(simpleFilters));
        }
      }

      const result = await getData(url.pathname + url.search, params.signal);
      if (result.success && result.data && Array.isArray(result.data)) {
        return result.data;
      } else {
        if (!isGetDataCancelledResult(result)) {
          logWarning('Failed to fetch single date table data:', getDataFailureReason(result));
        }
        return [];
      }
    } else {
      // Fallback to old config-based approach for backward compatibility
      const config = getManeuversConfig(context);
      const queryParams = config.buildQueryParams({
        className,
        projectId,
        eventType: params.eventType
      });

      let url: URL;
      if (config.apiEndpoints.table.startsWith('/') || config.apiEndpoints.table.startsWith('./')) {
        url = new URL(config.apiEndpoints.table, window.location.origin);
      } else {
        url = new URL(config.apiEndpoints.table);
      }
      Object.keys(queryParams).forEach(key => {
        url.searchParams.append(key, String(queryParams[key]));
      });

      const result = await getData(url.pathname + url.search, params.signal);
      if (result.success && result.data && Array.isArray(result.data)) {
        return result.data;
      } else {
        if (!isGetDataCancelledResult(result)) {
          logWarning('Failed to fetch table data:', getDataFailureReason(result));
        }
        return [];
      }
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return [];
    }
    logError('Error fetching table data:', error);
    return [];
  }
}

/**
 * Fetch map data for maneuvers
 */
export async function fetchMapData(
  context: ManeuversContext,
  params: ManeuversDataParams
): Promise<any[]> {
  try {
    debug('fetchMapData called - context:', context, 'eventList length:', params.eventList?.length || 0, 'has timeRange:', !!params.timeRange);
    
    // Use time-range endpoint only when caller explicitly provides timeRange (e.g. for initial load without event list).
    // When we have an event list (any size), always use event_list path so map data matches table filtering.
    // For >100 events we chunk and call event_list endpoint per batch to avoid URL length limits.
    const eventListLength = params.eventList?.length || 0;
    const useTimeRange = !!params.timeRange && eventListLength === 0;
    
    debug('fetchMapData - eventListLength:', eventListLength, 'useTimeRange:', useTimeRange, 'has timeRange param:', !!params.timeRange);
    
    if (useTimeRange) {
      // Use new history endpoint
      if (!params.timeRange || !params.timeRange.startDate || !params.timeRange.endDate) {
        logError('fetchMapData: timeRange is required when eventList.length > 100 but was not provided. eventList.length:', params.eventList?.length);
        // Fallback: use wide date range if not provided
        params.timeRange = {
          startDate: '2020-01-01',
          endDate: '2099-12-31'
        };
        logWarning('fetchMapData: Using fallback date range:', params.timeRange);
      }

      const className = selectedClassName();
      const projectId = selectedProjectId();
      
      if (!className || !projectId) {
        logWarning('fetchMapData: className and projectId are required');
        return [];
      }

      // Description is required for mapdata endpoint
      if (!params.description || params.description.trim() === '') {
        logWarning('fetchMapData: description is required for history mapdata queries');
        return [];
      }

      // Source names are required for history mapdata endpoint
      // Auto-populate if missing for fleet/historical contexts
      if (!params.sourceNames || params.sourceNames.length === 0) {
        if (context === 'fleet') {
          // Try to get sources from sourcesStore
          if (sourcesStore.isReady()) {
            const allSources = sourcesStore.sources();
            if (allSources && allSources.length > 0) {
              params.sourceNames = allSources.map((s: any) => s.source_name).filter((name: string) => name);
              log(`[maneuversDataService] fetchMapData - Auto-populated sourceNames for fleet context: ${JSON.stringify(params.sourceNames)}`);
            }
          }
        } else if (context === 'historical') {
          // Try to get source from selectedSourceId
          const { selectedSourceId } = persistantStore;
          const sourceId = selectedSourceId();
          if (sourceId && sourceId > 0) {
            const sources = sourcesStore.sources();
            const source = sources.find((s: any) => s.source_id === sourceId);
            if (source && source.source_name) {
              params.sourceNames = [source.source_name];
              log(`[maneuversDataService] fetchMapData - Auto-populated sourceNames for historical context: ${JSON.stringify(params.sourceNames)}`);
            }
          }
        }
        
        // Final check - still missing?
        if (!params.sourceNames || params.sourceNames.length === 0) {
          logWarning('fetchMapData: sourceNames is required for history mapdata queries');
          return [];
        }
      }

      const endpoint = `${apiEndpoints.app.data}/maneuvers-history-mapdata`;
      let url = new URL(endpoint, window.location.origin);
      
      url.searchParams.append('class_name', className);
      url.searchParams.append('project_id', String(projectId));
      url.searchParams.append('desc', params.description);
      url.searchParams.append('source_names', JSON.stringify(params.sourceNames));
      url.searchParams.append('start_date', params.timeRange.startDate);
      url.searchParams.append('end_date', params.timeRange.endDate);
      url.searchParams.append('event_type', params.eventType.toLowerCase());
      
      if (params.count) {
        url.searchParams.append('count', String(params.count));
      }
      
      if (params.filters && Object.keys(params.filters).length > 0) {
        url.searchParams.append('filters', JSON.stringify(params.filters));
      }

      const fullUrl = url.pathname + url.search;
      debug('fetchMapData - using history endpoint:', fullUrl);
      
      // Always log the full request URL and filters for debugging
      log(`[maneuversDataService] fetchMapData - Full request URL: ${fullUrl}`);
      if (params.filters) {
        log(`[maneuversDataService] fetchMapData - Filters: ${JSON.stringify(params.filters)}`);
      }
      if (params.timeRange) {
        log(`[maneuversDataService] fetchMapData - Time range: ${JSON.stringify(params.timeRange)}`);
      }
      
      const result = await getData(fullUrl, params.signal);
      debug('fetchMapData - API response success:', result.success, 'data length:', result.data?.length || 0);
      
      // Log full request details if no data returned (for debugging)
      if (result.success && (!result.data || result.data.length === 0)) {
        log(`[maneuversDataService] fetchMapData returned 0 results. Request: ${fullUrl}`);
        if (params.filters) {
          log(`[maneuversDataService] Filters applied: ${JSON.stringify(params.filters)}`);
        }
      }

      if (result.success && result.data && Array.isArray(result.data)) {
        return result.data;
      } else {
        if (!isGetDataCancelledResult(result)) {
          logError('Failed to fetch map data by range:', getDataFailureReason(result));
        }
        return [];
      }
    } else {
      // Use event_list endpoint (same criteria as table so selection matches map)
      if (!params.eventList || !Array.isArray(params.eventList) || params.eventList.length === 0) {
        logWarning('fetchMapData: eventList is required and must be non-empty. Skipping API call.');
        debug('fetchMapData - returning early: no eventList');
        return [];
      }

      const config = getManeuversConfig(context);
      const BATCH_SIZE = 100;
      const batches: number[][] = [];
      for (let i = 0; i < params.eventList.length; i += BATCH_SIZE) {
        batches.push(params.eventList.slice(i, i + BATCH_SIZE));
      }

      const allData: any[] = [];
      for (let b = 0; b < batches.length; b++) {
        const batch = batches[b];
        const queryParams = config.buildQueryParams({
          className: selectedClassName(),
          projectId: selectedProjectId(),
          eventType: params.eventType,
          description: params.description,
          eventList: batch
        });

        debug('fetchMapData - batch', b + 1, 'of', batches.length, 'eventList length:', batch.length);
        debug('fetchMapData - endpoint:', config.apiEndpoints.map);

        let url: URL;
        if (config.apiEndpoints.map.startsWith('/') || config.apiEndpoints.map.startsWith('./')) {
          url = new URL(config.apiEndpoints.map, window.location.origin);
        } else {
          url = new URL(config.apiEndpoints.map);
        }
        Object.keys(queryParams).forEach(key => {
          url.searchParams.append(key, String(queryParams[key]));
        });

        const result = await getData(url.pathname + url.search, params.signal);
        if (result.success && result.data && Array.isArray(result.data)) {
          allData.push(...result.data);
        } else if (!result.success) {
          if (isGetDataCancelledResult(result)) {
            break;
          }
          logError('Failed to fetch map data (batch ' + (b + 1) + '):', getDataFailureReason(result));
        } else {
          logWarning(
            'fetchMapData: batch ' + (b + 1) + ' reported success but `data` is not an array',
            { dataKind: result.data === null ? 'null' : result.data === undefined ? 'undefined' : Array.isArray(result.data) ? 'array' : typeof result.data }
          );
        }
      }

      debug('fetchMapData - total data length after chunks:', allData.length);
      return allData;
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return [];
    }
    logError('Error fetching map data:', error);
    return [];
  }
}

/**
 * Fetch time series data for maneuvers
 */
export async function fetchTimeSeriesData(
  context: ManeuversContext,
  params: ManeuversDataParams
): Promise<any[]> {
  try {
    // Use time-range endpoint only when caller explicitly provides timeRange and no event list.
    // When we have an event list (any size), always use event_list path so timeseries matches table filtering.
    // For >100 events we chunk and call event_list endpoint per batch.
    const eventListLength = params.eventList?.length || 0;
    const useTimeRange = !!params.timeRange && eventListLength === 0;
    
    debug('fetchTimeSeriesData - eventListLength:', eventListLength, 'useTimeRange:', useTimeRange, 'has timeRange param:', !!params.timeRange);
    
    if (useTimeRange) {
      // Use new history endpoint
      if (!params.timeRange || !params.timeRange.startDate || !params.timeRange.endDate) {
        logWarning('fetchTimeSeriesData: timeRange is required for range-based query. Skipping API call.');
        return [];
      }

      const className = selectedClassName();
      const projectId = selectedProjectId();
      
      if (!className || !projectId) {
        logWarning('fetchTimeSeriesData: className and projectId are required');
        return [];
      }

      // Description is required for timeseries endpoint
      if (!params.description || params.description.trim() === '') {
        logWarning('fetchTimeSeriesData: description is required for history timeseries queries');
        return [];
      }

      // Source names are required for history timeseries endpoint
      // Auto-populate if missing for fleet/historical contexts
      if (!params.sourceNames || params.sourceNames.length === 0) {
        if (context === 'fleet') {
          // Try to get sources from sourcesStore
          if (sourcesStore.isReady()) {
            const allSources = sourcesStore.sources();
            if (allSources && allSources.length > 0) {
              params.sourceNames = allSources.map((s: any) => s.source_name).filter((name: string) => name);
              log(`[maneuversDataService] fetchTimeSeriesData - Auto-populated sourceNames for fleet context: ${JSON.stringify(params.sourceNames)}`);
            }
          }
        } else if (context === 'historical') {
          // Try to get source from selectedSourceId
          const { selectedSourceId } = persistantStore;
          const sourceId = selectedSourceId();
          if (sourceId && sourceId > 0) {
            const sources = sourcesStore.sources();
            const source = sources.find((s: any) => s.source_id === sourceId);
            if (source && source.source_name) {
              params.sourceNames = [source.source_name];
              log(`[maneuversDataService] fetchTimeSeriesData - Auto-populated sourceNames for historical context: ${JSON.stringify(params.sourceNames)}`);
            }
          }
        }
        
        // Final check - still missing?
        if (!params.sourceNames || params.sourceNames.length === 0) {
          logWarning('fetchTimeSeriesData: sourceNames is required for history timeseries queries');
          return [];
        }
      }

      const endpoint = `${apiEndpoints.app.data}/maneuvers-history-timeseries`;
      let url = new URL(endpoint, window.location.origin);
      
      url.searchParams.append('class_name', className);
      url.searchParams.append('project_id', String(projectId));
      url.searchParams.append('desc', params.description);
      url.searchParams.append('source_names', JSON.stringify(params.sourceNames));
      url.searchParams.append('start_date', params.timeRange.startDate);
      url.searchParams.append('end_date', params.timeRange.endDate);
      url.searchParams.append('event_type', params.eventType.toLowerCase());
      
      if (params.count) {
        url.searchParams.append('count', String(params.count));
      }
      
      if (params.filters && Object.keys(params.filters).length > 0) {
        url.searchParams.append('filters', JSON.stringify(params.filters));
      }

      debug('fetchTimeSeriesData - using history endpoint:', url.pathname + url.search);
      const result = await getData(url.pathname + url.search, params.signal);

      if (result.success && result.data && Array.isArray(result.data)) {
        // Return object with data and charts if charts are available
        if (result.charts && Array.isArray(result.charts)) {
          return { data: result.data, charts: result.charts };
        }
        return result.data;
      } else {
        if (!isGetDataCancelledResult(result)) {
          logError('Failed to fetch time series data by range:', getDataFailureReason(result));
        }
        return [];
      }
    } else {
      // Use event_list endpoint (same criteria as table so selection matches timeseries)
      if (!params.eventList || !Array.isArray(params.eventList) || params.eventList.length === 0) {
        logWarning('fetchTimeSeriesData: eventList is required and must be non-empty. Skipping API call.');
        return [];
      }

      const config = getManeuversConfig(context);
      const BATCH_SIZE = 100;
      const batches: number[][] = [];
      for (let i = 0; i < params.eventList.length; i += BATCH_SIZE) {
        batches.push(params.eventList.slice(i, i + BATCH_SIZE));
      }

      const allData: any[] = [];
      let charts: string[] | undefined;
      for (let b = 0; b < batches.length; b++) {
        const batch = batches[b];
        const queryParams = config.buildQueryParams({
          className: selectedClassName(),
          projectId: selectedProjectId(),
          eventType: params.eventType,
          description: params.description,
          eventList: batch
        });

        debug('fetchTimeSeriesData - batch', b + 1, 'of', batches.length, 'eventList length:', batch.length);

        let url: URL;
        if (config.apiEndpoints.timeSeries.startsWith('/') || config.apiEndpoints.timeSeries.startsWith('./')) {
          url = new URL(config.apiEndpoints.timeSeries, window.location.origin);
        } else {
          url = new URL(config.apiEndpoints.timeSeries);
        }
        Object.keys(queryParams).forEach(key => {
          url.searchParams.append(key, String(queryParams[key]));
        });

        const result = await getData(url.pathname + url.search, params.signal);
        if (result.success && result.data && Array.isArray(result.data)) {
          allData.push(...result.data);
          if (result.charts && Array.isArray(result.charts) && !charts) {
            charts = result.charts;
          }
        } else if (!result.success) {
          if (isGetDataCancelledResult(result)) {
            break;
          }
          logError('Failed to fetch time series data (batch ' + (b + 1) + '):', getDataFailureReason(result));
        } else {
          logWarning(
            'fetchTimeSeriesData: batch ' + (b + 1) + ' reported success but `data` is not an array',
            { dataKind: result.data === null ? 'null' : result.data === undefined ? 'undefined' : Array.isArray(result.data) ? 'array' : typeof result.data }
          );
        }
      }

      debug('fetchTimeSeriesData - total data length after chunks:', allData.length);
      if (allData.length > 0) {
        if (charts && charts.length > 0) {
          return { data: allData, charts };
        }
        return allData;
      }
      return [];
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return [];
    }
    logError('Error fetching time series data:', error);
    return [];
  }
}

const MANEUVER_PHASES = ['full', 'investment', 'turn', 'acceleration'] as const;

/** Normalize phase for API query param (lowercase; default full). */
export function normalizeManeuverPhaseParam(phase: string | undefined): string {
  const p = String(phase ?? 'full').trim().toLowerCase();
  return (MANEUVER_PHASES as readonly string[]).includes(p) ? p : 'full';
}

export interface ManeuverTimeseriesChannelsResult {
  sample_event_id?: number;
  channels: string[];
  charts: string[];
}

export interface ManeuverTimeseriesValuesRow {
  event_id: number;
  values: Array<Record<string, unknown>>;
}

/**
 * Discover merged maneuver timeseries channel names for the current phase prefix.
 */
export async function fetchManeuverTimeseriesChannels(
  context: ManeuversContext,
  params: {
    eventType: string;
    phase?: string;
    signal?: AbortSignal;
  }
): Promise<ManeuverTimeseriesChannelsResult> {
  const className = selectedClassName();
  const projectId = selectedProjectId();
  if (!className || !projectId) {
    logWarning('fetchManeuverTimeseriesChannels: className and projectId are required');
    return { channels: [], charts: [] };
  }

  const { selectedDatasetId, selectedDate, selectedSourceName } = persistantStore;
  const url = new URL(`${apiEndpoints.app.data}/maneuvers-timeseries-channels`, window.location.origin);
  url.searchParams.append('class_name', className);
  url.searchParams.append('project_id', String(projectId));
  url.searchParams.append('event_type', params.eventType.toLowerCase());
  url.searchParams.append('phase', normalizeManeuverPhaseParam(params.phase));

  if (context === 'dataset') {
    url.searchParams.append('dataset_id', String(selectedDatasetId()));
  } else {
    const dateStr = selectedDate();
    const sourceName = selectedSourceName();
    if (!dateStr || !sourceName) {
      logWarning('fetchManeuverTimeseriesChannels: date and source_name required for non-dataset context');
      return { channels: [], charts: [] };
    }
    url.searchParams.append('date', dateStr);
    url.searchParams.append('source_names', JSON.stringify([sourceName]));
  }

  try {
    const result = await getData(url.pathname + url.search, params.signal);
    if (result.status === 204) {
      return { channels: [], charts: [] };
    }
    if (result.success && result.data) {
      const data = result.data as ManeuverTimeseriesChannelsResult;
      return {
        sample_event_id: data.sample_event_id,
        channels: Array.isArray(data.channels) ? data.channels : [],
        charts: Array.isArray(data.charts) ? data.charts : [],
      };
    }
    if (!isGetDataCancelledResult(result)) {
      logError('fetchManeuverTimeseriesChannels failed:', getDataFailureReason(result));
    }
    return { channels: [], charts: [] };
  } catch (error: unknown) {
    const name = error && typeof error === 'object' && 'name' in error ? String((error as { name?: string }).name) : '';
    if (name !== 'AbortError') {
      logError('fetchManeuverTimeseriesChannels error:', error);
    }
    return { channels: [], charts: [] };
  }
}

/**
 * Fetch channel-filtered maneuver timeseries values (batched at 100 event ids).
 */
export async function fetchManeuverTimeseriesValues(
  params: {
    eventType: string;
    phase?: string;
    eventList: number[];
    channels: string[];
    signal?: AbortSignal;
  }
): Promise<ManeuverTimeseriesValuesRow[]> {
  const className = selectedClassName();
  const projectId = selectedProjectId();
  if (!className || !projectId) {
    logWarning('fetchManeuverTimeseriesValues: className and projectId are required');
    return [];
  }

  const eventIds = [...new Set(params.eventList.filter((id) => Number.isFinite(id) && id > 0))];
  const channels = [...new Set(params.channels.map((c) => String(c).trim()).filter(Boolean))];
  if (eventIds.length === 0 || channels.length === 0) {
    return [];
  }

  const BATCH_SIZE = 100;
  const allRows: ManeuverTimeseriesValuesRow[] = [];

  for (let i = 0; i < eventIds.length; i += BATCH_SIZE) {
    const batch = eventIds.slice(i, i + BATCH_SIZE);
    const url = new URL(`${apiEndpoints.app.data}/maneuvers-timeseries-values`, window.location.origin);
    url.searchParams.append('class_name', className);
    url.searchParams.append('project_id', String(projectId));
    url.searchParams.append('phase', normalizeManeuverPhaseParam(params.phase));
    url.searchParams.append('event_list', JSON.stringify(batch));
    url.searchParams.append('channels', JSON.stringify(channels));

    try {
      const result = await getData(url.pathname + url.search, params.signal);
      if (result.status === 204) continue;
      if (result.success && Array.isArray(result.data)) {
        allRows.push(...(result.data as ManeuverTimeseriesValuesRow[]));
      } else if (!isGetDataCancelledResult(result)) {
        logError('fetchManeuverTimeseriesValues batch failed:', getDataFailureReason(result));
      }
    } catch (error: unknown) {
      const name = error && typeof error === 'object' && 'name' in error ? String((error as { name?: string }).name) : '';
      if (name === 'AbortError') break;
      logError('fetchManeuverTimeseriesValues error:', error);
    }
  }

  return allRows;
}

/** Channel discovery for FileChannelPicker maneuver mode. */
export async function fetchManeuverTimeseriesChannelNames(
  context: ManeuversContext,
  params: {
    eventType: string;
    phase?: string;
    signal?: AbortSignal;
  }
): Promise<string[]> {
  const result = await fetchManeuverTimeseriesChannels(context, params);
  return result.channels;
}
